import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShowChart,
  Analytics,
  Notifications,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const AdditionalMetricsSection = ({ detectionData }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const metrics = [
    {
      titleKey: 'downtrend.additionalMetrics.priceChange30d',
      description: t('downtrend.additionalMetrics.priceChange30dDesc', 'Percentage price change compared to 30 days ago'),
      value:
        detectionData.price_change_30d !== null &&
        detectionData.price_change_30d !== undefined
          ? `${detectionData.price_change_30d >= 0 ? '+' : ''}${detectionData.price_change_30d.toFixed(2)}%`
          : t('downtrend.riskLabels.na'),
      color:
        detectionData.price_change_30d >= 0
          ? theme.palette.success.main
          : theme.palette.error.main,
      icon: detectionData.price_change_30d >= 0 ? <TrendingUp /> : <TrendingDown />,
      gradient: detectionData.price_change_30d >= 0
        ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
        : `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
    },
    {
      titleKey: 'downtrend.additionalMetrics.volatility30d',
      description: t('downtrend.additionalMetrics.volatility30dDesc', 'Price volatility over 30 days (higher = riskier)'),
      value:
        detectionData.volatility_30d !== null &&
        detectionData.volatility_30d !== undefined
          ? `${detectionData.volatility_30d.toFixed(2)}%`
          : t('downtrend.riskLabels.na'),
      color: theme.palette.warning.main,
      icon: <ShowChart />,
      gradient: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
    },
    {
      titleKey: 'downtrend.additionalMetrics.relativeStrength',
      description: t('downtrend.additionalMetrics.relativeStrengthDesc', 'Stock price / VN-Index ratio. >1 = outperforming, <1 = underperforming'),
      value:
        detectionData.relative_strength !== null &&
        detectionData.relative_strength !== undefined
          ? detectionData.relative_strength.toFixed(4)
          : t('downtrend.riskLabels.na'),
      color: theme.palette.info.main,
      icon: <Analytics />,
      gradient: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
    },
    {
      titleKey: 'downtrend.additionalMetrics.alertsGenerated',
      description: t('downtrend.additionalMetrics.alertsGeneratedDesc', 'Number of alerts generated from analysis'),
      value: detectionData.alerts_generated || 0,
      color: theme.palette.error.main,
      icon: <Notifications />,
      gradient: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
    },
  ];

  return (
    <Box sx={{ mb: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.25,
        }}
      >
        {metrics.map((metric, idx) => (
          <Tooltip
            key={idx}
            title={metric.description}
            arrow
            placement="top"
          >
            <Card
            elevation={0}
            sx={{
              border: `1px solid ${alpha(metric.color, 0.15)}`,
              borderRadius: 1.8,
              bgcolor: alpha(metric.color, 0.03),
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                bgcolor: alpha(metric.color, 0.06),
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 14px ${alpha(metric.color, 0.12)}`,
                borderColor: alpha(metric.color, 0.3),
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: metric.gradient,
              },
            }}
          >
            <CardContent sx={{ p: 1.5, pt: 1.7 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.9 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.35,
                    background: metric.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 3px 10px ${alpha(metric.color, 0.22)}`,
                  }}
                >
                  {React.cloneElement(metric.icon, { sx: { color: 'white', fontSize: 20 } })}
                </Box>
                
                {/* Title */}
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.64rem', 
                    fontWeight: 600, 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                    lineHeight: 1.3,
                  }}
                >
                  {t(metric.titleKey)}
                </Typography>
                
                {/* Value */}
                <Typography 
                  fontWeight={800} 
                  sx={{ 
                    color: metric.color, 
                    fontFamily: 'monospace',
                    fontSize: '1.15rem',
                    lineHeight: 1,
                  }}
                >
                  {metric.value}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default AdditionalMetricsSection;
